/**
 * <bundle-selector>
 *
 * Powers the "Buy Single / Buy a Pair / Buy 3" widget rendered by snippets/bundle-selector.liquid.
 *
 * Responsibilities:
 *  - Keep the bundle UI in sync with the main variant picker (when the customer changes the variant, the bundle
 *    selections follow, unless the customer has manually edited a given dropdown).
 *  - Resolve the chosen color/size (+ inherited fit) of each bundle line item to a real variant id.
 *  - Reflect the current selection into the product form so that the existing <product-form> submit handler adds the
 *    correct line item(s): one for "single", two for "pair", three for "triple".
 *  - Display combined bundle pricing using the configured discount percentages (display only — the real discount must
 *    be set up as automatic discounts in the Shopify admin).
 */
(function () {
  const moneyFormat = (window.themeVariables && window.themeVariables.settings.moneyFormat) || '${{amount}}';

  // How many line items each mode adds to the cart.
  const MODE_COUNT = { single: 1, pair: 2, triple: 3 };

  function formatMoney(cents) {
    cents = Math.round(Number(cents) || 0);

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';

      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, placeholder) {
      switch (placeholder) {
        case 'amount':
          return withDelimiters(cents, 2);
        case 'amount_no_decimals':
          return withDelimiters(cents, 0);
        case 'amount_with_comma_separator':
          return withDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(cents, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return withDelimiters(cents, 0, ' ', '');
        case 'amount_with_space_separator':
          return withDelimiters(cents, 2, ' ', ',');
        default:
          return withDelimiters(cents, 2);
      }
    });
  }

  class BundleSelector extends HTMLElement {
    connectedCallback() {
      this.formId = this.getAttribute('form-id');
      this.form = document.forms[this.formId];

      this.discounts = {
        pair: parseFloat(this.getAttribute('pair-discount')) || 0,
        triple: parseFloat(this.getAttribute('triple-discount')) || 0,
      };

      try {
        this.variants = JSON.parse(this.querySelector('[data-bundle-variants]').textContent);
      } catch (e) {
        this.variants = [];
      }

      const selectedId = parseInt(this.getAttribute('selected-variant-id'), 10);
      this.currentVariant = this.variants.find((v) => v.id === selectedId) || this.variants[0];

      this.addLabel = this.getAttribute('add-label') || 'Add to cart';
      this.soldoutLabel = this.getAttribute('soldout-label') || 'Sold out';

      this.modeInputs = Array.from(this.querySelectorAll('.bundle-selector__radio-input'));
      this.cards = Array.from(this.querySelectorAll('[data-bundle-card]'));
      this.itemSelects = Array.from(this.querySelectorAll('.bundle-select__input'));
      this.priceEls = {
        single: this.querySelector('[data-single-price]'),
        pair: this.querySelector('[data-pair-price]'),
        triple: this.querySelector('[data-triple-price]'),
      };
      this.noticeEl = this.querySelector('[data-bundle-notice]');

      // Track which dropdowns the customer has manually edited (key: "mode-itemIndex") so we don't overwrite them on
      // variant change.
      this.dirty = {};

      this.modeInputs.forEach((input) =>
        input.addEventListener('change', () => this.refresh())
      );

      // Clicking anywhere on a card selects that bundle option — except when the click lands on a dropdown, so that
      // choosing a colour/size does not change which bundle is selected.
      this.cards.forEach((card) =>
        card.addEventListener('click', (event) => {
          if (event.target.closest('.bundle-select')) return;
          this.selectMode(card.getAttribute('data-bundle-card'));
        })
      );

      this.itemSelects.forEach((select) =>
        select.addEventListener('change', () => {
          const key = select.getAttribute('data-bundle-mode') + '-' + select.getAttribute('data-bundle-item');
          this.dirty[key] = true;
          this.refresh();
        })
      );

      // The main variant picker dispatches "variant:change" on its form.
      if (this.form) {
        this.form.addEventListener('variant:change', (event) => this.onVariantChange(event));
      }

      this.renderSinglePrice();
      this.refresh();
    }

    get mode() {
      const checked = this.modeInputs.find((input) => input.checked);
      return checked ? checked.value : 'single';
    }

    get submitButton() {
      return this.form ? this.form.querySelector('buy-buttons [type="submit"]') : null;
    }

    selectMode(mode) {
      const radio = this.modeInputs.find((input) => input.value === mode);
      if (!radio || radio.checked) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;
      this.currentVariant = variant;

      // Push the new color/size into any dropdown the customer hasn't touched yet.
      this.itemSelects.forEach((select) => {
        const key = select.getAttribute('data-bundle-mode') + '-' + select.getAttribute('data-bundle-item');
        if (this.dirty[key]) return;
        const position = parseInt(select.getAttribute('data-option-position'), 10);
        const value = variant.options[position - 1];
        if (value != null && select.value !== value) {
          select.value = value;
        }
      });

      this.renderSinglePrice();
      this.refresh();
    }

    // Recompute pricing, sync the form line items, reconcile the add-to-cart button, and reflect the selected state.
    refresh() {
      this.renderModePrice('pair');
      this.renderModePrice('triple');
      this.updateSelectedState();
      this.updateFormItems();
      this.updateAvailability();
    }

    updateSelectedState() {
      const mode = this.mode;
      this.cards.forEach((card) =>
        card.classList.toggle('is-selected', card.getAttribute('data-bundle-card') === mode)
      );
    }

    /**
     * Resolve one bundle line item to a variant by combining its selectable option values (from the dropdowns of the
     * given mode + item index) with the fit inherited from the currently selected main variant.
     */
    resolveItem(mode, itemIndex) {
      if (!this.currentVariant) return null;
      const desired = this.currentVariant.options.slice();

      this.itemSelects.forEach((select) => {
        if (select.getAttribute('data-bundle-mode') !== mode) return;
        if (select.getAttribute('data-bundle-item') !== String(itemIndex)) return;
        const position = parseInt(select.getAttribute('data-option-position'), 10);
        desired[position - 1] = select.value;
      });

      return this.variants.find((variant) => variant.options.every((value, index) => value === desired[index])) || null;
    }

    // Returns the resolved variant for each line item of a mode (entries may be null if a combination doesn't exist).
    resolveItems(mode) {
      if (mode === 'single') return [this.currentVariant];
      const count = MODE_COUNT[mode] || 0;
      const items = [];
      for (let i = 0; i < count; i++) {
        items.push(this.resolveItem(mode, i));
      }
      return items;
    }

    renderSinglePrice() {
      const el = this.priceEls.single;
      if (!el || !this.currentVariant) return;
      const compare = this.currentVariant.compare_at_price;
      el.innerHTML = this.priceMarkup(this.currentVariant.price, compare > 0 ? compare : null);
    }

    renderModePrice(mode) {
      const el = this.priceEls[mode];
      if (!el) return;

      const items = this.resolveItems(mode).map((variant) => variant || this.currentVariant);
      if (items.some((variant) => !variant)) return;

      const compare = items.reduce((sum, variant) => sum + variant.price, 0);
      const discount = this.discounts[mode] || 0;
      const sale = Math.round((compare * (100 - discount)) / 100);

      el.innerHTML = this.priceMarkup(sale, discount > 0 ? compare : null);
    }

    priceMarkup(saleCents, compareCents) {
      const onSale = compareCents != null && compareCents > saleCents;
      let html = '<price-list class="price-list">';
      html += `<sale-price class="${onSale ? 'text-on-sale' : 'text-subdued'} text-sm">${formatMoney(saleCents)}</sale-price>`;
      if (onSale) {
        html += `<compare-at-price class="text-subdued text-sm line-through">${formatMoney(compareCents)}</compare-at-price>`;
      }
      html += '</price-list>';
      return html;
    }

    /**
     * Reflect the current bundle choice into the product form. In "single" mode the native hidden `id` input is used.
     * In "pair"/"triple" mode the `id` input is disabled and one `items[n]` pair of inputs is injected per line item so
     * cart/add.js adds them all.
     */
    updateFormItems() {
      if (!this.form) return;
      const idInput = this.form.elements['id'];
      const mode = this.mode;

      this.clearInjectedInputs();

      if (mode !== 'single') {
        const items = this.resolveItems(mode);

        // Only wire the multi-item add when every line resolves to an in-stock variant. Otherwise we leave the form on
        // the single fallback and the add-to-cart button is disabled by updateAvailability().
        if (items.length && items.every((variant) => variant && variant.available)) {
          if (idInput) idInput.disabled = true;
          items.forEach((variant, index) => this.injectItem(index, variant.id, 1));
          return;
        }
      }

      // Single mode (or the bundle could not be resolved): fall back to the native single-variant add.
      if (idInput) {
        idInput.disabled = false;
        if (this.currentVariant) idInput.value = this.currentVariant.id;
      }
    }

    /**
     * Keep the add-to-cart button and sold-out messaging in sync with the current selection.
     * In single mode the theme owns the button (it re-renders it on variant change), so we only undo any override we
     * previously applied. In bundle modes the button reflects the availability of every chosen line item.
     */
    updateAvailability() {
      const btn = this.submitButton;
      const mode = this.mode;

      this.clearItemMarks();

      if (mode === 'single') {
        if (btn) this.restoreButton(btn);
        this.hideNotice();
        return;
      }

      const items = this.resolveItems(mode);
      const unavailable = [];

      items.forEach((variant, index) => {
        const isUnavailable = !variant || !variant.available;
        this.markItemSelects(mode, index, isUnavailable);
        if (isUnavailable) unavailable.push(variant);
      });

      const ok = unavailable.length === 0;
      if (btn) this.setBundleButton(btn, ok);

      if (ok) {
        this.hideNotice();
      } else {
        const names = unavailable.map((variant) => (variant ? `'${variant.title}'` : 'this combination'));
        const subject = names.join(' and ');
        const verb = unavailable.length > 1 ? 'are' : 'is';
        this.showNotice(`${subject} ${verb} sold out — please pick a different colour or size for the bundle.`);
      }
    }

    setBundleButton(btn, available) {
      if (!btn.dataset.bundleOrig) {
        btn.dataset.bundleOrig = '1';
        btn.dataset.bundleOrigLabel = this.getButtonLabel(btn);
        btn.dataset.bundleOrigDisabled = btn.disabled ? '1' : '0';
        btn.dataset.bundleOrigClass = btn.className;
      }
      btn.disabled = !available;
      btn.classList.toggle('button--subdued', !available);
      this.setButtonLabel(btn, available ? this.addLabel : this.soldoutLabel);
    }

    restoreButton(btn) {
      if (!btn.dataset.bundleOrig) return;
      this.setButtonLabel(btn, btn.dataset.bundleOrigLabel);
      btn.disabled = btn.dataset.bundleOrigDisabled === '1';
      btn.className = btn.dataset.bundleOrigClass;
      delete btn.dataset.bundleOrig;
      delete btn.dataset.bundleOrigLabel;
      delete btn.dataset.bundleOrigDisabled;
      delete btn.dataset.bundleOrigClass;
    }

    /**
     * The add-to-cart button is a <button is="custom-button"> whose label lives in a child <div> next to a
     * <span class="button__loader">. We must update ONLY the label text — replacing the button's whole content would
     * destroy the loader element and break the add-to-cart animation.
     */
    getButtonLabelTarget(btn) {
      return btn.querySelector(':scope > div') || btn;
    }

    getButtonLabel(btn) {
      return this.getButtonLabelTarget(btn).textContent;
    }

    setButtonLabel(btn, text) {
      this.getButtonLabelTarget(btn).textContent = text;
    }

    markItemSelects(mode, itemIndex, unavailable) {
      this.itemSelects.forEach((select) => {
        if (select.getAttribute('data-bundle-mode') !== mode) return;
        if (select.getAttribute('data-bundle-item') !== String(itemIndex)) return;
        if (unavailable) {
          select.setAttribute('data-unavailable', '');
        } else {
          select.removeAttribute('data-unavailable');
        }
      });
    }

    clearItemMarks() {
      this.itemSelects.forEach((select) => select.removeAttribute('data-unavailable'));
    }

    showNotice(message) {
      if (!this.noticeEl) return;
      this.noticeEl.textContent = message;
      this.noticeEl.hidden = false;
    }

    hideNotice() {
      if (!this.noticeEl) return;
      this.noticeEl.hidden = true;
    }

    injectItem(index, variantId, quantity) {
      this.form.appendChild(this.hiddenInput(`items[${index}][id]`, variantId));
      this.form.appendChild(this.hiddenInput(`items[${index}][quantity]`, quantity));
    }

    hiddenInput(name, value) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.setAttribute('data-bundle-injected', '');
      return input;
    }

    clearInjectedInputs() {
      this.form.querySelectorAll('input[data-bundle-injected]').forEach((input) => input.remove());
    }
  }

  if (!window.customElements.get('bundle-selector')) {
    window.customElements.define('bundle-selector', BundleSelector);
  }
})();
